import API from '../../../../API/API'
import { asArray, asText, authHeaders } from '../Shared/apiHelpers'

/**
 * Both directions of the product attribute screen are served by one GET. The two
 * directions differ only in which schema list they show, which field list sits on
 * each side, and the RequestType used when saving.
 */
export const SOURCE_TO_TARGET = {
    key: 'SourceToTarget',
    requestType: 'Source',
    schemaListKey: 'sourceMappedSchemaList',
    leftIsErp: true
}

export const TARGET_TO_SOURCE = {
    key: 'TargetToSource',
    requestType: 'Target',
    schemaListKey: 'targetMappedSchemaList',
    leftIsErp: false
}

/** A selectable field from sourceMethods / targetMethods. */
const toField = (method) => ({
    name: asText(method.name),
    dataType: asText(method.dataType),
    isRequired: Boolean(method.isRequired),
    size: asText(method.size),
    type: asText(method.type),
    // Comma separated list of data types this field may not be mapped to.
    notAllowed: asText(method.notAllowed)
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== ''),
    isDefault: Boolean(method.default)
})

const toFields = (data) => asArray(data).map(toField).filter((field) => field.name !== '')

/** One saved mapping, kept in the API's own field names so saving is a straight echo. */
const toMapping = (item) => ({
    erpAttributeName: asText(item.erpAttributeName),
    erpAttributeDataType: asText(item.erpAttributeDataType),
    erpIsRequired: Boolean(item.erpIsRequired),
    erpAttributeSize: asText(item.erpAttributeSize),
    erpAttributeType: asText(item.erpAttributeType),
    ecommerceAttributeName: asText(item.ecommerceAttributeName),
    ecommerceAttributeDataType: asText(item.ecommerceAttributeDataType),
    ecommerceIsRequired: Boolean(item.ecommerceIsRequired),
    ecommerceAttributeSize: asText(item.ecommerceAttributeSize),
    ecommerceAttributeType: asText(item.ecommerceAttributeType),
    default: Boolean(item.default)
})

export const getProductAttributes = (connectorId, instancesId, direction, config) =>
    API.get(`/Connection/${connectorId}/instance/${instancesId}/product-attribute`, { headers: authHeaders(), ...config })
        .then((response) => {
            const data = response.data || {}
            return {
                mappings: asArray(data[direction.schemaListKey]).map(toMapping),
                erpFields: toFields(data.sourceMethods),
                ecommerceFields: toFields(data.targetMethods)
            }
        })

/** Payload shape expected by POST .../save-product-mappings/{RequestType}. */
const toRequestBody = (mapping) => ({
    erpAttributeName: mapping.erpAttributeName,
    erpAttributeDataType: mapping.erpAttributeDataType,
    erpIsRequired: mapping.erpIsRequired,
    erpAttributeSize: mapping.erpAttributeSize,
    erpAttributeType: mapping.erpAttributeType,
    ecommerceAttributeName: mapping.ecommerceAttributeName,
    ecommerceAttributeDataType: mapping.ecommerceAttributeDataType,
    ecommerceIsRequired: mapping.ecommerceIsRequired,
    ecommerceAttributeSize: mapping.ecommerceAttributeSize,
    ecommerceAttributeType: mapping.ecommerceAttributeType,
    default: mapping.default,
    canUpdate: true,
    canDelete: true
})

export const saveProductMappings = (connectorId, instancesId, direction, mappings) =>
    API.post(
        `/Connection/${connectorId}/instance/${instancesId}/save-product-mappings/${direction.requestType}`,
        { [direction.schemaListKey]: mappings.map(toRequestBody) },
        { headers: authHeaders() }
    ).then((response) => response.data)
